import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import IssueCodeContent from './issue-codes-content/IssueCodesContent';
import TextAreaComponent from '../../common/TextAreaComponent/TextAreaComponent';
import {useReviewHeaderContext} from '../../header/ReviewHeader';

import TabsCommonComponent, {
  ITabsData,
} from '../../common/tabs-component/TabsCommonComponent';
import './ReviewAndApproveContent.scss';
import {IssueCode} from '../../../../../api/types/aiReview.type';
interface ReviewAndApproveContentProps {
  caseContext: string;
  setCaseContext: Dispatch<SetStateAction<string>>;
  relevanceCriteria: string;
  setRelevanceCriteria: Dispatch<SetStateAction<string>>;
  issueCodeData: IssueCode[];
  setIssueCodeData: Dispatch<SetStateAction<IssueCode[]>>;
}

const ReviewAndApproveContent = ({
  caseContext,
  setCaseContext,
  relevanceCriteria,
  setRelevanceCriteria,
  issueCodeData,
  setIssueCodeData,
}: ReviewAndApproveContentProps) => {
  const [showEmptyErrors, setShowEmptyErrors] = useState(false);
  const [touchedTile, setTouchedTile] = useState<Record<string, boolean>>({});

  const {setHasValidationErrors} = useReviewHeaderContext();

  const handleTileBlur = (id: string) => {
    setTouchedTile((prev) => ({...prev, [id]: true}));
  };

  const invalidCharRegex = useMemo(() => /[<>*;'/()#&]/, []);

  const nameErrors = useMemo(() => {
    return issueCodeData.reduce<Record<string, boolean>>((acc, tile) => {
      acc[tile.id!] = invalidCharRegex.test(tile.code);
      return acc;
    }, {});
  }, [issueCodeData, invalidCharRegex]);

  const hasValidationErrors = useMemo(() => {
    return issueCodeData.some(
      (tile) =>
        tile.code?.trim() === '' ||
        tile.question?.trim() === '' ||
        nameErrors[tile.id!]
    );
  }, [issueCodeData, nameErrors]);

  useEffect(() => {
    setHasValidationErrors(hasValidationErrors);
  }, [hasValidationErrors]);

  const handleCaseContextChange = (value: string) => {
    if (value.length <= 10000) setCaseContext(value);
  };

  const handleRelevanceCriteriaChange = (value: string) => {
    if (value.length <= 10000) setRelevanceCriteria(value);
  };

  const tabData: ITabsData[] = [
    {
      id: 1,
      label: t('ai.review.reviewAndApprove.caseContext'),
      content: (
        <TextAreaComponent
          data-testid="contextAndCriteria-caseContext-input"
          value={caseContext}
          maxLength={10000}
          helperText={`${caseContext.length.toLocaleString()} / 10,000`}
          onChange={handleCaseContextChange}
          displayOptionalLabel={false}
          className="review-approve-content"
          childrenClassName="children-content"
        />
      ),
    },
    {
      id: 2,
      label: t('ai.review.reviewAndApprove.relevanceCriteria'),
      content: (
        <TextAreaComponent
          data-testid="contextAndCriteria-relevanceCriteria-input"
          value={relevanceCriteria}
          maxLength={10000}
          helperText={`${relevanceCriteria.length.toLocaleString()} / 10,000`}
          onChange={handleRelevanceCriteriaChange}
          displayOptionalLabel={false}
          className="review-approve-content relevance-criteria"
          childrenClassName="children-content relevance-criteria-container"
        />
      ),
    },
    {
      id: 3,
      label: t('ai.review.reviewAndApprove.issueCodes'),
      content: (
        <Box className="tab-content-placeholder">
          <IssueCodeContent
            issueCodeData={issueCodeData}
            setIssueCodeData={setIssueCodeData}
            nameErrors={nameErrors}
            hasValidationErrors={hasValidationErrors}
            showEmptyErrors={showEmptyErrors}
            setShowEmptyErrors={setShowEmptyErrors}
            touchedTile={touchedTile}
            handleTileBlur={handleTileBlur}
          />
        </Box>
      ),
    },
  ];
  return <TabsCommonComponent tabsData={tabData} />;
};

export default ReviewAndApproveContent;
